import { useMemo } from "react";
import { useList, useNavigation } from "@refinedev/core";
import type { ColumnDef } from "@tanstack/react-table";
import { Plus } from "@/components/ui/icon";

import type { Phase, Process } from "@aos/shared";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefLink } from "@/components/inspector";
import { RowActions } from "@/resources/_shared/ListScaffold";
import { ResourceTable } from "../skills/_components/ResourceTable";

/**
 * Phases list — the shared `ResourceTable` (PageHeader + table + canonical
 * states). The parent process is shown by **name** (resolved through a one-shot
 * process lookup → RefLink), never a raw `process_id`; markers render as quiet
 * chips. No id column.
 */
export function PhaseList() {
  const { create, edit, show } = useNavigation();

  // One-shot lookup so the Process column shows the process name, not its id.
  const { data: processData } = useList<Process>({
    resource: "processes",
    pagination: { pageSize: 200, mode: "server" },
  });
  const processNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of processData?.data ?? []) map.set(p.id, p.name);
    return map;
  }, [processData]);

  const columns = useMemo<ColumnDef<Phase>[]>(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: "Name",
        cell: ({ getValue }) => (
          <span className="font-medium">{getValue() as string}</span>
        ),
      },
      {
        id: "process_id",
        accessorKey: "process_id",
        header: "Process",
        enableSorting: false,
        cell: ({ row }) => (
          <RefLink
            resource="processes"
            id={row.original.process_id}
            label={processNames.get(row.original.process_id)}
          />
        ),
      },
      {
        id: "sort_order",
        accessorKey: "sort_order",
        header: "Order",
        cell: ({ getValue }) => (getValue() as number | null) ?? "—",
      },
      {
        id: "markers",
        header: "Markers",
        enableSorting: false,
        cell: ({ row }) =>
          row.original.is_start || row.original.is_terminal ? (
            <div className="flex gap-1">
              {row.original.is_start ? (
                <Badge variant="secondary">start</Badge>
              ) : null}
              {row.original.is_terminal ? (
                <Badge variant="secondary">terminal</Badge>
              ) : null}
            </div>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <RowActions
              onShow={() => show("phases", row.original.id)}
              onEdit={() => edit("phases", row.original.id)}
            />
          </div>
        ),
      },
    ],
    [show, edit, processNames],
  );

  return (
    <ResourceTable<Phase>
      resource="phases"
      title="Phases"
      subtitle="Ordered steps within a process, with start and terminal markers."
      columns={columns}
      initialSort={{ field: "created_at", order: "desc" }}
      emptyMessage="No phases yet."
      toolbar={
        <Button onClick={() => create("phases")}>
          <Plus className="mr-2 h-4 w-4" />
          New Phase
        </Button>
      }
    />
  );
}

export default PhaseList;
